import { format } from 'date-fns';
import React from 'react';

import { useLanguage } from '../../../hooks/useLanguage.js';
import { cx } from '../../../utils/cx.js';

import { formatDateWithLanguage } from '../../../utils/get-date';
import { getPremiereDate, isPremiereLocked } from '../../../utils/premiere';
import { capitalizeFirstLetter } from '../../../utils/string.js';
import { translate } from '../../../utils/wp.js';

import { PackageData } from './PackageData';
import { PackageTickets } from './PackageTickets';

function getSpecialNight(pkg, discreet = false) {
    if (
        !NOJESRESOR_PRODUCT.special_nights ||
        NOJESRESOR_PRODUCT.special_nights.length === 0
    ) {
        return null;
    }

    const packageDate = format(new Date(pkg.ticket.date), 'yyyy-MM-dd');
    const packageTime = pkg.ticket.time + ':00';

    const special_nights = NOJESRESOR_PRODUCT.special_nights;
    for (let i = 0, l = special_nights.length, night; i < l; ++i) {
        night = special_nights[i];
        if ((discreet && !night.discreet) ||
            (!discreet && night.discreet)) {
            continue;
        }

        if (night.date === packageDate) {
            if (night.time != null && night.time.length > 0) {
                return night.time === packageTime
                    ? night
                    : null;
            } else {
                return night;
            }
        }
    }

    return null;
}

function checkIfCanceled(pkg) {
    if (NOJESRESOR_PRODUCT.meta.event_is_canceled) {
        return true;
    }

    return getCanceledDate(pkg);
}

function getCanceledDate(pkg) {
    const packageDate = format(new Date(pkg.ticket.date), 'yyyy-MM-dd');

    return NOJESRESOR_PRODUCT.meta.canceled_dates
        .find(d => d.date === packageDate);
}

export function Package(props) {
    const [language] = useLanguage();

    const ticketDate = props.package.ticket.date;
    const day = capitalizeFirstLetter(formatDateWithLanguage(
        ticketDate,
        'eeee',
        language,
    ));
    const date = formatDateWithLanguage(ticketDate, 'd', language);
    const time = props.package.ticket.time;
    const arena = props.package.venue.arena;
    const city = (props.package.venue.cities != null)
        ? props.package.venue.cities[0].title
        : null;
    const alternatives = props.package.ticket.alternativ;
    const multidate = props.package.ticket.multidate;

    let selected_desktop_ticket = props.selectedTicket;
    if (selected_desktop_ticket == null) {
        for (let i = 0, l = props.package.tickets.length; i < l; ++i) {
            if (props.package.tickets[i].stock > 0) {
                selected_desktop_ticket = props.package.tickets[i];
                break;
            }
        }
    }

    const special = getSpecialNight(props.package);
    const discreetSpecial = getSpecialNight(props.package, true);

    const isLockedByPremiere = isPremiereLocked(props.package.tickets);
    const premiereDate = getPremiereDate(props.package.tickets);

    const isSelected = props.selectedPackage != null &&
        props.selectedPackage.ticket.id === props.package.ticket.id;
    const isCanceled = checkIfCanceled(props.package);

    const isInactive = props.inactive ||
        isLockedByPremiere ||
        !props.package.available;

    const isPackage = !NOJESRESOR_PRODUCT.no_package &&
        !NOJESRESOR_PRODUCT.hotels.optional &&
        !NOJESRESOR_PRODUCT.hotels.without;

    const classNames = ['package box p-4 mb-2 bg-white rounded flex flex-wrap items-start gap-4 sm:items-center'];
    if (special && !special.discreet) {
        classNames.push('special');
    }

    if (isInactive) {
        classNames.push('opacity-50 pointer-events-none');
    }

    function onClick() {
        if (
            isLockedByPremiere ||
            isCanceled ||
            isSelected ||
            !props.package.available
        ) {
            return;
        }

        onSelect();
    }

    function onSelect() {
        if (props.inactive) {
            return;
        }

        props.onSelectPackage(props.package);
    }

    return (
        <div
            className={classNames.join(' ')}
            data-cy="package"
            onClick={onClick}
        >
            <div className="w-full flex flex-wrap items-center gap-4">
                <div className="border border-primary rounded basis-16">
                    <div
                        className={cx(
                            'py-1.5 bg-primary flex items-center justify-center font-bold text-primarytext',
                            multidate ? 'text-small text-center' : 'text-2xl',
                        )}
                    >
                        {multidate ? translate('multi_date') : date}
                    </div>
                    <div className="month-name py-1.5 flex items-center justify-center font-bold text-[10px] leading-[1.1]">
                        {formatDateWithLanguage(
                            ticketDate,
                            'LLL yyyy',
                            language,
                        )}
                    </div>
                </div>
                <PackageData
                    className="flex-1 flex flex-wrap items-center gap-4 package__data"
                    multiday={NOJESRESOR_PRODUCT.multiday}
                    packagePrice={props.package.price}
                    ticketPrice={props.package.ticket.price}
                    isPackage={isPackage}
                    isSoldOut={!props.package.available}
                    isCanceled={isCanceled}
                    canceledDate={getCanceledDate(props.package)}
                    isPremiereLocked={isLockedByPremiere}
                    premiereDate={premiereDate}
                    alternatives={translate(alternatives)}
                    day={day}
                    time={time}
                    arena={arena}
                    city={city}
                    special={discreetSpecial}
                />
                {!isLockedByPremiere && (
                    <>
                        {!isCanceled && isSelected && (
                            <button
                                className="basis-full inline-flex items-center justify-center gap-x-2 rounded-full px-6 py-3 text-xs font-bold bg-red text-black sm:basis-auto sm:ml-auto"
                                onClick={props.onDeselectPackage}
                            >
                                {translate('undo_selection')}
                            </button>
                        )}
                        {!isCanceled && !isSelected &&
                            props.package.available &&
                            (
                                <button
                                    className="basis-full inline-flex items-center justify-center gap-x-2 rounded-full px-6 py-3 text-xs font-bold bg-primary text-primarytext sm:basis-auto sm:ml-auto"
                                >
                                    {translate('select_date')}
                                </button>
                            )}
                        {!isCanceled && !isSelected &&
                            !props.package.available &&
                            (
                                <button
                                    className="basis-full inline-flex items-center justify-center gap-x-2 rounded-full px-6 py-3 text-xs font-bold bg-primary bg-red text-black sm:basis-auto sm:ml-auto"
                                >
                                    {translate('sold_out')}
                                </button>
                            )}
                    </>
                )}
            </div>
            {isSelected &&
                <>
                    <PackageTickets
                        confirmedTickets={props.confirmedTickets}
                        addedTickets={props.addedTickets}
                        ticketTypes={props.ticketTypes}
                        selectedTicket={props.selectedTicket}
                        selectedTicketType={props.selectedTicketType}
                        removeTicket={props.removeTicket}
                        addTicket={props.addTicket}
                        onSelectTicket={props.onSelectTicket}
                        onSelectTicketType={props.onSelectTicketType}
                        package={props.package}
                        selectedPackage={props.selectedPackage}
                        confirmedPackage={props.confirmedPackage}
                        special={special}
                        onConfirmTickets={props.onConfirmTickets}
                        onDeselectPackage={props.onDeselectPackage}
                    />
                </>
            }
        </div>
    );
}
